<template>
  <div class="results">
    <Filters
      v-if="filters"
      :competition-data="competitionData"
      :filters="filters"
      :current-filters="currentFilters"
      :after-stage="afterStage"
      :collapsed-all="collapsedAll"
      @switch-penalties="$emit('switch-penalties')"
      @clear-current-filters="clearCurrentFilters"
      @toggle-details="collapsedAll = !collapsedAll"
    />

    <div v-if="results.length" class="table-container">
      <table>
        <thead>
          <tr>
            <th class="pos is-center">{{ $t("pos") }}</th>
            <th class="num is-center">{{ $t("no") }}</th>
            <th class="driver">{{ $t("driver") }}</th>
            <th v-if="competitionData.competition[0].pilot" class="driver">{{ $t("co-driver") }}</th>
            <th class="car">{{ $t("car") }}</th>
            <th class="num is-center" v-if="[6, 7].includes(currentStage.type)">{{ $t("coefficient") }}</th>
            <th class="time is-center" v-if="[0, 1, 2, 3, 6, 7].includes(currentStage.type)">
              {{ $t("time")
              }}<span v-if="currentStage.type === 2 || currentStage.type === 3">
                => {{ parseInt(currentStage.data) | formatResult(resultPrecision) }}</span
              >
            </th>
            <th class="time is-center" v-else>{{ $t("points") }}</th>
            <th class="avg-speed">{{ $t("avgSpeed") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(result, index) in filteredResults" :key="result.competitorId">
            <td :colspan="colspan">
              <table>
                <tr class="row has-action" @click="toggleCollapse" :class="{ 'is-new': isNew(result) }">
                  <td class="pos is-center">
                    {{ (index + 1) | formatPosition(index, filteredResults, currentStage, afterStage) }}
                    <span v-if="promotion(index) > 0" style="color: green">▲</span>
                    <span v-if="promotion(index) < 0" style="color: red">▼</span>
                  </td>
                  <td class="num is-center">
                    {{ result.number }}
                  </td>
                  <td class="driver">
                    <div>
                      <country-flag :country="getFlag(result.driver.nationality)" size="small" class="flag" />
                      {{ result.driver.name }} {{ result.driver.surname }}
                    </div>
                  </td>
                  <td v-if="competitionData.competition[0].pilot" class="driver">
                    <div v-if="result.pilot">
                      <country-flag :country="getFlag(result.pilot.nationality)" size="small" class="flag" />
                      {{ result.pilot.name }} {{ result.pilot.surname }}
                    </div>
                    <div v-if="result.pilot2">
                      <country-flag :country="getFlag(result.pilot2.nationality)" size="small" class="flag" />
                      {{ result.pilot2.name }} {{ result.pilot2.surname }}
                    </div>
                  </td>
                  <td class="car">
                    {{ result.car }}
                  </td>
                  <td class="num is-center" v-if="[6, 7].includes(currentStage.type)">
                    {{ result.coefficient }}
                  </td>
                  <td class="time is-center">
                    <template v-if="[0, 1, 6, 7].includes(currentStage.type)">
                      <template v-if="afterStage">
                        {{ totalResult(result) | formatResult(resultPrecision) }}
                        <a class="penalty" v-if="competitionData.competition[0].penalties && hasPenalty(result)">
                          (P)
                        </a>
                      </template>
                      <template v-else>
                        {{ result.result | formatResult(resultPrecision) }}
                        <a class="penalty" v-if="competitionData.competition[0].penalties && result.penalty > 0">
                          +{{ result.penalty | formatShortResult(resultPrecision) }}
                        </a>
                      </template>
                    </template>
                    <template v-else-if="[2, 3].includes(currentStage.type)">
                      {{ result.result | formatRegularityResult(resultPrecision, currentStage.data) }}
                      <br /><a style="font-size: 80%">{{ result.result.realTime | formatResult(resultPrecision) }}</a>
                    </template>
                    <template v-else-if="[4, 5].includes(currentStage.type)">
                      <span v-if="result.result.points">
                        {{ result.result.points | formatPoints }}
                      </span>
                      <span v-else>
                        {{ result.result }}
                      </span>
                    </template>
                  </td>
                  <td class="avg-speed">
                    {{ result.avgSpeed | formatSpeed }}
                  </td>
                </tr>
                <tr class="collapse" :class="{ 'is-active': !collapsedAll }">
                  <td :colspan="colspan">
                    <div class="collapse__container">
                      <div>
                        <dl class="row-details-list">
                          <div class="item">
                            <dt>{{ $t("competitor") }}</dt>
                            <dd>
                              {{ result.competitor }}
                              <div>
                                {{ getClubName(result.driver.clubId) }}
                                <template v-if="competitionData.competition[0].pilot">
                                  <span v-if="result.pilot">
                                    / {{ getClubName(result.pilot.clubId) }}
                                    <template v-if="result.pilot2">
                                      / {{ getClubName(result.pilot2.clubId) }}
                                    </template>
                                  </span>
                                </template>
                              </div>
                            </dd>
                          </div>

                          <div v-if="currentChampionship.byCategory" class="item">
                            <dt>{{ $t("category") }}</dt>
                            <dd>
                              {{
                                getNameById(
                                  result.championshipParticipation[currentChampionship.id].categoryId,
                                  "category"
                                )
                              }}
                            </dd>
                          </div>

                          <div v-if="currentChampionship.byGroup" class="item">
                            <dt>{{ $t("group") }}</dt>
                            <dd>
                              {{
                                getNameById(result.championshipParticipation[currentChampionship.id].groupId, "group")
                              }}
                            </dd>
                          </div>

                          <div v-if="currentChampionship.byClass" class="item">
                            <dt>{{ $t("class") }}</dt>
                            <dd>
                              {{
                                getNameById(result.championshipParticipation[currentChampionship.id].classId, "class")
                              }}
                            </dd>
                          </div>

                          <div v-if="currentChampionship.bySpecial" class="item">
                            <dt>{{ $t("specialClassification") }}</dt>
                            <dd>
                              <ul>
                                <li
                                  v-if="
                                    result.championshipParticipation[currentChampionship.id].specialIds.length === 0
                                  "
                                >
                                  -
                                </li>
                                <li
                                  v-for="specialId in result.championshipParticipation[currentChampionship.id]
                                    .specialIds"
                                  :key="specialId"
                                >
                                  {{ getNameById(specialId, "specialClassification") }}
                                </li>
                              </ul>
                            </dd>
                          </div>

                          <div v-if="result.splitTimes.length > 0" class="item">
                            <dt>{{ $t("splitTimes") }}</dt>
                            <dd>
                              <ul v-if="[0, 1, 2, 3, 6, 7].includes(currentStage.type)">
                                <li v-for="(splitTime, index) in result.splitTimes" :key="index">
                                  {{ index + 1 }}:
                                  {{ splitTime | formatResult(resultPrecision) }}
                                </li>
                              </ul>
                              <ul v-else-if="[4, 5].includes(currentStage.type)">
                                <li v-for="(splitTime, index) in result.splitTimes" :key="index">
                                  <template v-if="splitTime">
                                    {{ index }}:
                                    <span v-if="splitTime">
                                      {{ splitTime.time | formatResult(resultPrecision) }} |
                                      <span :class="[splitTime.speed > splitTime.maxSpeed ? 'error-color' : '']"
                                        >{{ splitTime.speed | formatSpeed }} </span
                                      >|
                                      {{ splitTime.points | formatPoints }}
                                    </span>
                                    <span v-else> - </span>
                                  </template>
                                </li>
                                <li>
                                  M:
                                  <span v-if="result.result.meta">
                                    {{ result.result.meta.time | formatResult(resultPrecision) }} |
                                    <span
                                      :class="[
                                        result.result.meta.speed > result.result.meta.maxSpeed ? 'error-color' : '',
                                      ]"
                                      >{{ result.result.meta.speed | formatSpeed }} </span
                                    >|
                                    {{ result.result.meta.points | formatPoints }}
                                  </span>
                                  <span v-else> - </span>
                                </li>
                              </ul>
                            </dd>
                          </div>

                          <div
                            v-if="afterStage && competitionData.competition[0].penalties && hasPenalty(result)"
                            class="item"
                          >
                            <dt>{{ $t("penalties") }}</dt>
                            <dd>
                              <ul>
                                <li>
                                  {{ $t("penaltiesSport") }}:
                                  {{ result.penaltyIn | formatPenaltyValue(resultPrecision) }}
                                </li>
                                <li>
                                  {{ $t("penaltiesOther") }}:
                                  {{ result.penaltyOut | formatPenaltyValue(resultPrecision) }}
                                </li>
                              </ul>
                            </dd>
                          </div>

                          <div v-if="[6, 7].includes(currentStage.type)" class="item">
                            <dt>{{ $t("realTime") }}</dt>
                            <dd>
                              {{ result.realResult | formatResult(resultPrecision) }}
                            </dd>
                          </div>

                          <div v-if="!isNaN(result.result)" class="item">
                            <dt>{{ $t("toFirst") }}</dt>
                            <dd v-if="index > 0">
                              {{
                                (result.result +
                                  (result.penalty || 0) -
                                  filteredResults[0].result -
                                  (filteredResults[0].penalty || 0))
                                  | formatShortResult(resultPrecision)
                              }}
                            </dd>
                            <dd v-else>-</dd>
                          </div>

                          <div v-if="!isNaN(result.result)" class="item">
                            <dt>{{ $t("toPrev") }}</dt>
                            <dd v-if="index > 0">
                              {{
                                (result.result +
                                  (result.penalty || 0) -
                                  filteredResults[index - 1].result -
                                  (filteredResults[index - 1].penalty || 0))
                                  | formatShortResult(resultPrecision)
                              }}
                            </dd>
                            <dd v-else>-</dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="empty">{{ $t("noResults") }}</div>
  </div>
</template>

<script>
import CountryFlag from "vue-country-flag"
import Filters from "@/components/Filters.vue"

function formatShortResult(result, resultPrecision) {
  if (isNaN(result)) return result
  if (result === null) return "-"

  const ms = result % 1000
  const sec = ((result - ms) / 1000) % 60
  const min = (((result - ms) / 1000 - sec) / 60) % 60
  const hrs = (((result - ms) / 1000 - sec) / 60 - min) / 60

  const format = (input) => String(input).padStart(2, "0")

  const formatMs = (ms) => String(ms).padStart(3, "0").slice(0, resultPrecision)
  if (hrs > 0) return `${hrs}:${format(min)}:${format(sec)},${formatMs(ms)}`
  if (min > 0) return `${min}:${format(sec)},${formatMs(ms)}`
  return `${sec},${formatMs(ms)}`
}

export default {
  name: "Results",

  components: {
    CountryFlag,
    Filters,
  },

  filters: {
    formatPosition: function (position, index, array, currentStage, afterStage) {
      // W widoku "po odcinku" kolumna czasu pokazuje czas z karą, więc remis też liczymy po tej sumie.
      const total = (result) =>
        afterStage && !isNaN(result.result) ? result.result + (result.penalty || 0) : result.result

      switch (currentStage.type) {
        case 0:
        case 1:
        case 6:
        case 7:
          if (isNaN(array[index].result)) return "NC"
          // if(index>0 && Math.floor(array[index].result/100) === Math.floor(array[index-1].result/100))
          if (index > 0 && total(array[index]) === total(array[index - 1])) return "="
          break
        case 2:
        case 3:
        case 4:
        case 5:
      }
      return position < 10 ? `0${position}` : position
    },
    formatResult: function (result, resultPrecision) {
      if (isNaN(result)) return result
      if (result === null) return "-"

      const ms = result % 1000
      const sec = ((result - ms) / 1000) % 60
      const min = (((result - ms) / 1000 - sec) / 60) % 60
      const hrs = (((result - ms) / 1000 - sec) / 60 - min) / 60
      const format = (input) => String(input).padStart(2, "0")
      const formatMs = (ms) => String(ms).padStart(3, "0").slice(0, resultPrecision)
      if (hrs > 0) return `${hrs}:${format(min)}:${format(sec)},${formatMs(ms)}`

      return `${format(min)}:${format(sec)},${formatMs(ms)}`
    },
    formatRegularityResult: function (result, resultPrecision, targetTime) {
      if (isNaN(result.time)) return result
      if (result.time === null) return "-"

      const ms = result.time % 1000
      const sec = ((result.time - ms) / 1000) % 60
      const min = ((result.time - ms) / 1000 - sec) / 60

      const format = (input) => String(input).padStart(2, "0")
      let timeSign = "+"
      if (targetTime > result.realTime) timeSign = "-"
      const formatMs = (ms) => String(ms).padStart(3, "0").slice(0, resultPrecision)
      if (min > 0) return `${timeSign} ${min}:${format(sec)},${formatMs(ms)}`
      return `${timeSign} ${sec},${formatMs(ms)}`
    },
    formatShortResult,
    // Kara równa zero to po prostu brak kary — pokazujemy kreskę zamiast "0,0".
    formatPenaltyValue: function (penalty, resultPrecision) {
      if (!penalty) return "-"
      return formatShortResult(penalty, resultPrecision)
    },
    formatSpeed: function (speed) {
      return speed ? `${speed} km/h` : "–"
    },
    formatPoints: function (points) {
      return points ? points / 1000 : "0"
    },
  },

  props: {
    competitionData: Object,
    filters: Array,
    results: Array,
    resultPrecision: Number,
    currentChampionship: Object,
    currentStage: Object,
    lastTime: Object,
    clubs: Array,
    afterStage: Boolean,
  },
  data() {
    return {
      collapsedAll: true,
      currentFilters: [],
    }
  },
  computed: {
    filteredResults: function () {
      let filteredResults = this.extraSortedResults
      for (let index in this.filters) {
        const currentFilter = this.currentFilters[index]
        if (currentFilter)
          switch (this.filters[index].key) {
            case "club":
              if (currentFilter.value)
                filteredResults = filteredResults.filter(
                  (result) =>
                    result.driver?.clubId === currentFilter.value ||
                    result.pilot?.clubId === currentFilter.value ||
                    result.pilot2?.clubId === currentFilter.value
                )
              break
            case "nationality":
              if (currentFilter.value)
                filteredResults = filteredResults.filter(
                  (result) =>
                    result.driver?.nationality === currentFilter.value ||
                    result.pilot?.nationality === currentFilter.value ||
                    result.pilot2?.nationality === currentFilter.value
                )
              break
            case "category":
              if (currentFilter.id)
                filteredResults = filteredResults.filter(
                  (result) =>
                    result.championshipParticipation[this.currentChampionship.id].categoryId === currentFilter.id
                )
              break
            case "group":
              if (currentFilter.id)
                filteredResults = filteredResults.filter(
                  (result) => result.championshipParticipation[this.currentChampionship.id].groupId === currentFilter.id
                )
              break
            case "class":
              if (currentFilter.id)
                filteredResults = filteredResults.filter(
                  (result) => result.championshipParticipation[this.currentChampionship.id].classId === currentFilter.id
                )
              break
            case "specialClassification":
              if (currentFilter.id)
                filteredResults = filteredResults.filter((result) =>
                  result.championshipParticipation[this.currentChampionship.id].specialIds.includes(currentFilter.id)
                )
              break
          }
      }
      return filteredResults
    },
    extraSortedResults: function () {
      const results = this.results
      if (!this.currentStage) return results
      if ([6, 7].includes(this.currentStage.type)) {
        return results
          .map((competitorResult) => {
            const actualCompetitorChampionship = competitorResult.championshipParticipation[this.currentChampionship.id]
            const numberA = Math.floor(actualCompetitorChampionship.data / 1000) / 100
            const numberB = (actualCompetitorChampionship.data % 1000) / 100
            let countedResult = competitorResult.result
            let chosenCoefficient = "1,00"
            if (!isNaN(countedResult)) {
              switch (this.coefficientType) {
                case 0:
                  countedResult *= 1
                  chosenCoefficient = "1,00"
                  break
                case 1:
                  chosenCoefficient = numberA.toFixed(2).replace(".", ".")
                  countedResult =
                    Math.floor((countedResult * numberA) / this.precisionDivNumber) * this.precisionDivNumber
                  break
                case 2:
                  chosenCoefficient = numberB.toFixed(2).replace(".", ".")
                  countedResult =
                    Math.floor((countedResult * numberB) / this.precisionDivNumber) * this.precisionDivNumber
                  break
              }
            }
            // console.log(competitorResult.number, actualCompetitorChampionship.data, numberA, numberB, chosenCoefficient)
            return { ...competitorResult, result: countedResult, coefficient: chosenCoefficient }
          })
          .sort((a, b) => {
            if (isNaN(a.result) && !isNaN(b.result)) return 1
            if (!isNaN(a.result) && isNaN(b.result)) return -1
            return this.competitionData.competition[0].penalties
              ? a.result + a.penalty - (b.result + b.penalty)
              : a.result - b.result
          })
      }
      return results
    },
    precisionDivNumber: function () {
      switch (this.resultPrecision) {
        case 3:
          return 1
        case 2:
          return 10
        case 1:
          return 100
      }
      return 1
    },
    coefficientType: function () {
      if (![6, 7].includes(this.currentStage.type)) return 0
      if (this.currentChampionship?.id === 61) return 2

      for (let index in this.filters) {
        const currentFilter = this.currentFilters[index]
        if (currentFilter)
          switch (this.filters[index].key) {
            case "specialClassification":
              if (currentFilter.id && currentFilter.data !== "6") {
                console.log("specClass - ", currentFilter.data)
                return 0
              }
              break
            case "class":
              if (currentFilter.id) {
                // console.log("class - 2")
                return 2
              }
              break
          }
      }
      return 1
    },
    colspan: function () {
      let colspan = 6
      if (this.competitionData.competition[0].pilot) colspan++
      if ([6, 7].includes(this.currentStage.type)) colspan++
      return colspan
    },
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentFilters = []
        this.clearCurrentFilters()
      },
    },
  },
  mounted: function () {
    this.clearCurrentFilters()
  },

  methods: {
    toggleCollapse: (event) => {
      const collapse = event.target.closest(".has-action").nextSibling
      collapse.classList.toggle("is-active")
    },

    clearCurrentFilters: function () {
      for (let index in this.filters)
        this.currentFilters.splice(index, 1, { name: this.$i18n.t("everybody"), default: true })
    },

    getNameById: function (id, propertyName) {
      if (this.competitionData) return this.competitionData[propertyName].find((el) => el.id === id)?.name || "-"
      return "-"
    },

    getClubName: function (id) {
      if (!id) return "NZ"
      return this.competitionData.club.find((club) => club.id === id).shortName
    },
    // Czas pokazywany w widoku "po odcinku" — surowy wynik powiększony o karę wliczaną do klasyfikacji.
    totalResult: function (result) {
      if (isNaN(result.result)) return result.result
      return result.result + (result.penalty || 0)
    },

    // Znacznik (P) stawiamy przy jakiejkolwiek karze, także takiej, która nie weszła do czasu.
    hasPenalty: function (result) {
      return (result.penaltyIn || 0) + (result.penaltyOut || 0) > 0
    },

    isNew: function (time) {
      return (
        this.lastTime &&
        this.currentStage.stageNo === this.lastTime.stageNo &&
        time.competitorId === this.lastTime.competitorId
      )
    },
    promotion: function (index) {
      if (
        !this.afterStage ||
        this.currentStage.stageNo === 1 ||
        (this.currentStage.type !== 0 && this.currentStage.type !== 6)
      )
        return 0
      const time = this.filteredResults[index].previousResult
      if (isNaN(time)) return 0
      const penalty = this.filteredResults[index].previousPenalty
      const howMuchBetter = this.filteredResults.filter(
        (result) => result.previousResult + result.previousPenalty < time + penalty
      ).length
      return howMuchBetter - index
    },
    getFlag: function (value) {
      if (value === "KOS") return "xk"
      return value
    },
    // filterString: () => {},
    // filterNationality: () => {},
    // filterCategory: () => {},
    // filterGroup: () => {},
    // filterClass: () => {},
  },
}
</script>

<style scoped lang="scss">
.results {
  margin: 1rem;

  .table-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

.empty {
  color: #2b2b2b;
  text-align: center;
  font-weight: bold;
  font-size: 0.75rem;
  text-transform: uppercase;
  background: #fff;
  padding: 2rem;
}

// Base table styles:

table {
  min-width: 800px;
  table-layout: fixed;
}

// Table column modifiers:

tr.has-action {
  cursor: pointer;
  transition: background 250ms;

  &:hover {
    background: rgba(#fff, 0.75);
  }
}

// Specific columns styles:

.pos {
  width: 3rem;
}

td.pos {
  font-weight: bold;
  font-size: 1.15rem;
  white-space: nowrap;

  span {
    font-size: 0.65rem;
    display: inline-block;
    vertical-align: middle;
    line-height: normal;
  }
}

.num {
  width: 3rem;
}

.driver {
  div {
    display: flex;
    align-items: center;

    .flag {
      flex-shrink: 0;
      box-shadow: 0 0 0.5rem rgba(#000, 0.25);
      margin: -0.5rem -1rem;
    }
  }
}

.time {
  .penalty {
    font-size: 80%;
    color: red;
  }
}

// Single result row:

.row {
  background: #fff;
  font-size: 0.85rem;

  td {
    padding: 0.25rem 0.5rem;
  }
}

// Collapse:

.collapse {
  visibility: collapse;
  transition: visibility 200ms;

  &__container {
    font-size: 0.65rem;
    background: rgba(#fff, 0.5);
    visibility: collapse;
    transition: all 250ms;
    max-height: 0;
    opacity: 0;
    padding: 0;

    & > div {
      padding: 0.2rem 1rem;
    }
  }

  &.is-active {
    visibility: visible;
  }

  &.is-active &__container {
    max-height: 15rem;
    visibility: visible;
    transition: all 250ms;
    opacity: 1;
  }
}

.row-details-list {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  //grid-gap: 1rem;
  //grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));

  dt {
    text-transform: uppercase;
    opacity: 0.65;
  }

  dd {
    font-weight: bold;
  }

  .item {
    flex-grow: 1;
    width: 8rem;
    min-width: 8rem;
  }

  :first-child {
    flex-grow: 2;
  }
}

.is-new {
  transition: background-color 0.5s ease;
  background-color: #ffffbf;
}

.error-color {
  color: red;
}
</style>
